import "react-app-polyfill/ie9";
import "react-app-polyfill/ie11";
import "react-app-polyfill/stable";
import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import "react-virtualized/styles.css";
import "./index.css";
// eslint-disable-next-line import/no-unresolved
import "intro.js/introjs.css";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { CircularProgress, ThemeProvider } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { createTheme } from "@mui/material/styles";
import { PersistGate } from "redux-persist/integration/react";
import KeycloakService from "./services/KeycloakService";
import Root from "./Root";
import * as serviceWorker from "./serviceWorker";
import superstore from "./store";
import NotSupported from "./views/not-supported/NotSupported";
import TokenRefreshComponent from "./services/TokenRefreshComponent";
import { applyFavicon } from "./common/utils/apply-favicon";
import { getWhiteLabelConfig } from "./common/config/whitelabel";

applyFavicon(getWhiteLabelConfig().favicon);

const theme = createTheme({
	palette: {
		primary: {
			main: "#3880e8",
		},
		secondary: {
			main: "rgb(224, 85, 56)",
		},
	},
	fontFamily: ["Roboto", "sans-serif", "-apple-system", "Arial"].join(","),
	components: {
		MuiTableCell: {
			styleOverrides: {
				root: {
					borderBottom: "1px solid var(--border-subtle)",
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				notchedOutline: {
					borderColor: "var(--border-default)",
				},
				root: {
					height: "32px",
					"&:hover:not(.Mui-focused):not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
						borderColor: "var(--border-strong)",
					},
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: "var(--border-focus)",
					},
					"&.Mui-error .MuiOutlinedInput-notchedOutline": {
						borderColor: "var(--border-error)",
					},
					"&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
						borderColor: "var(--border-disabled)",
					},
				},
				sizeSmall: {
					height: "24px",
				},
				multiline: {
					height: "auto",
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					color: "var(--text-primary)",
					"&.Mui-disabled": {
						color: "var(--text-disabled)",
					},
				},
				input: {
					"&::placeholder": {
						color: "var(--text-secondary)",
						opacity: 1,
					},
					"&.Mui-disabled": {
						WebkitTextFillColor: "var(--text-disabled)",
					},
				},
			},
		},
	},
});
export const muiCache = createCache({
	key: "mui",
	prepend: true,
});

export function isChromium(navigator) {
	const brands = navigator.userAgentData?.brands;
	if (!brands) {
		return false;
	}
	return brands.some((brand) => brand.brand === "Chromium" && brand.version > 89);
}
const isSupported = isChromium(window.navigator);
const container = document.getElementById("root");
const root = createRoot(container);

if (process.env.REACT_APP_PUBLIC_POSTHOG_KEY !== "null" && process.env.REACT_APP_PUBLIC_POSTHOG_HOST !== "null") {
	posthog.init(process.env.REACT_APP_PUBLIC_POSTHOG_KEY, {
		api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
		autocapture: false,
		capture_pageview: false,
		capture_performance: false,
		advanced_disable_feature_flags: true,
		disable_session_recording: true,
	});
}

const renderApp = () => {
	if (isSupported || window.env.REACT_APP_ENV === "dev" || window.env.REACT_APP_ENV === "qa") {
		root.render(
			<Provider store={superstore.store}>
				<TokenRefreshComponent />
				<BrowserRouter>
					<PersistGate
						loader={
							<div style={{ margin: "auto" }}>
								<CircularProgress color="primary" />
							</div>
						}
						persistor={superstore.persistor}
					>
						<CacheProvider value={muiCache}>
							<ThemeProvider theme={theme}>
								<LocalizationProvider dateAdapter={AdapterDateFns}>
									<PostHogProvider client={posthog}>
										<Root />
									</PostHogProvider>
								</LocalizationProvider>
							</ThemeProvider>
						</CacheProvider>
					</PersistGate>
				</BrowserRouter>
			</Provider>
		);
	} else {
		root.render(<NotSupported />);
	}
};

const defaultLanguage = localStorage.getItem("lang");
KeycloakService.initKeycloak(renderApp, defaultLanguage);

serviceWorker.unregister();
